import type { StudioPathLike } from '@sanity/client/csm';
import Icon from '../../generic/Icon';
import useOptimisticArray from '../../utils/useOptimisticArray';
import useDataAttribute from '../infrastructure/DataAttributeContext';
import SimpleRichText from '../richTextComponents/SimpleRichText';
import type { KeyedArray } from '../types';
import * as styles from './Accordion.module.css';
import LinedHeading from './LinedHeading';

export interface AccordionGroupProps {
    title?: string;
    items?: KeyedArray<AccordionProps>;
    studioPath?: StudioPathLike;
    context?: string;
}

export function AccordionGroup({ title, items, studioPath, context }: AccordionGroupProps) {
    const dataAttr = useDataAttribute();
    const list = useOptimisticArray(items, studioPath, dataAttr);
    if (!list.length) return null;
    return (
        <div className={styles.accordionGroup} data-sanity={studioPath && dataAttr?.(studioPath)}>
            <LinedHeading>{title}</LinedHeading>
            {list.filter(Boolean).map(({ title, text, _key }) => (
                <Accordion
                    key={_key}
                    title={title}
                    text={text}
                    studioPath={studioPath && dataAttr?.combine({ path: studioPath })([{ _key }])}
                    context={context}
                />
            ))}
        </div>
    );
}

export interface AccordionProps {
    title: string;
    text: any;
    studioPath?: string;
    context?: string;
}

export function Accordion({ title, text, studioPath, context }: AccordionProps) {
    const dataAttr = useDataAttribute();
    return (
        <details className={styles.accordion} data-sanity={studioPath && dataAttr?.(studioPath)} data-toggle-context={context}>
            <summary>
                <span>{title}</span>
                <Icon className="icon-details"></Icon>
            </summary>
            <div>
                <SimpleRichText value={text} studioPath={studioPath} />
            </div>
        </details>
    );
}
